/**
 * NER-4488 fase 3 — Aplicar una carta inicial por rubro.
 *
 * `buildStarterPlan` resuelve el catálogo a nombres reales (i18n) y
 * `applyStarterPlan` lo ejecuta contra la API existente (createCategory,
 * createItem, toggleItemAvailability, createModifierGroup) de forma
 * secuencial, saltando lo que ya existe por nombre y reportando progreso.
 * Sin endpoints nuevos: reutiliza el CRUD actual (y por lo tanto el tracking
 * de uso / límites de plan de cada item).
 */

import { getStarterMenu } from '../data/starterMenus';
import { getModifierTemplate } from '../data/modifierTemplates';
import { templateToGroup } from './modifierTemplates';
import { normalizeText } from './text';

/**
 * Plan resuelto: `{ id, categories: [{ name, preparationArea, items: [{ name, price }] }], groups: [...] }`.
 * @param {string} starterId - id del rubro (`STARTER_MENUS`)
 * @param {Function} t - función i18n
 */
export function buildStarterPlan(starterId, t) {
  const starter = getStarterMenu(starterId);
  if (!starter) return null;
  return {
    id: starter.id,
    categories: starter.categories.map((cat, index) => ({
      name: t(`starterMenus.categories.${cat.key}`),
      preparationArea: cat.preparationArea,
      displayOrder: index + 1,
      items: cat.items.map((item) => ({
        name: t(`starterMenus.items.${item.key}`),
        price: item.price,
      })),
    })),
    groups: (starter.modifierTemplateIds || [])
      .map((id) => templateToGroup(getModifierTemplate(id), t))
      .filter(Boolean),
  };
}

/** Cantidad de pasos (para la barra de progreso): categorías + items + grupos. */
export function countPlanSteps(plan) {
  if (!plan) return 0;
  const items = plan.categories.reduce((acc, cat) => acc + cat.items.length, 0);
  return plan.categories.length + items + (plan.groups || []).length;
}

const sameName = (a, b) => normalizeText(a).trim() === normalizeText(b).trim();

/**
 * Ejecuta el plan contra la API. `apis` inyecta las funciones (testeable):
 *   { createCategory(data), createItem(data), toggleItemAvailability(itemId),
 *     createModifierGroup(restaurantId, data) }
 * `existing` trae el estado actual: `{ categories: [{categoryId,name,items}], modifierGroups: [{name}] }`.
 * `onProgress(done, total, label)` se llama tras cada paso.
 *
 * Items creados quedan NO disponibles (toggle tras crear) para revisar precios.
 * Si un item/categoría/grupo ya existe (mismo nombre), se salta.
 * Ante un error de item (p. ej. límite de plan) se detiene y devuelve `stoppedAt`.
 */
export async function applyStarterPlan(plan, { apis, existing, restaurantId, onProgress }) {
  const total = countPlanSteps(plan);
  let done = 0;
  const summary = {
    categoriesCreated: 0,
    categoriesReused: 0,
    itemsCreated: 0,
    itemsSkipped: 0,
    groupsCreated: 0,
    groupsSkipped: 0,
    errors: [],
    stoppedAt: null,
  };
  const report = (label) => {
    done += 1;
    if (onProgress) onProgress(done, total, label);
  };

  const currentCategories = [...(existing?.categories || [])];
  const displayOrderBase = currentCategories.length;

  for (const cat of plan.categories) {
    let categoryId;
    const found = currentCategories.find((c) => sameName(c.name, cat.name));
    if (found) {
      categoryId = found.categoryId;
      summary.categoriesReused += 1;
    } else {
      try {
        const created = await apis.createCategory({
          name: cat.name,
          displayOrder: displayOrderBase + cat.displayOrder,
          preparationArea: cat.preparationArea,
          translations: {},
        });
        categoryId = created?.categoryId;
        summary.categoriesCreated += 1;
        currentCategories.push({ categoryId, name: cat.name, items: [] });
      } catch (error) {
        summary.errors.push({ step: 'category', name: cat.name, error });
        summary.stoppedAt = cat.name;
        return summary;
      }
    }
    report(cat.name);

    const existingItems = (found && found.items) || [];
    for (const item of cat.items) {
      if (existingItems.some((it) => sameName(it.name, item.name))) {
        summary.itemsSkipped += 1;
        report(item.name);
        continue;
      }
      try {
        const created = await apis.createItem({
          categoryId,
          name: item.name,
          price: item.price,
          preparationTime: 15,
        });
        if (created?.itemId && apis.toggleItemAvailability) {
          await apis.toggleItemAvailability(created.itemId);
        }
        summary.itemsCreated += 1;
      } catch (error) {
        summary.errors.push({ step: 'item', name: item.name, error });
        summary.stoppedAt = item.name;
        return summary;
      }
      report(item.name);
    }
  }

  const existingGroups = existing?.modifierGroups || [];
  for (const group of plan.groups || []) {
    if (existingGroups.some((g) => sameName(g.name, group.name))) {
      summary.groupsSkipped += 1;
      report(group.name);
      continue;
    }
    try {
      await apis.createModifierGroup(restaurantId, group);
      summary.groupsCreated += 1;
    } catch (error) {
      // Los grupos son opcionales: registrar y seguir.
      summary.errors.push({ step: 'group', name: group.name, error });
    }
    report(group.name);
  }

  return summary;
}
